/**
 * RadioGrid — server-backed list of all radios the current user can see.
 *
 * Used on the desktop Search view (all authenticated users). The built-in
 * search box queries ICCID + MAC via filter[search] on the server. Filters
 * mirror the Inventory Dashboard's placement cards (On site, Not installed,
 * Customer/Group inventory) and add an Online quick filter + customer/group
 * cascade picker for staff. The Placement chip and smart Location column
 * follow the dashboard grid's pattern so the two surfaces read alike.
 *
 * Columns, order, sort and visibility come from the radios grid-spec
 * descriptor via <EntityGrid>; this view supplies only the render overrides
 * (ICCID online dot + staff link, placement chip, Location link, relative
 * timestamp), the bespoke Onboard action column, the mobile card, the
 * caller-owned filter bar (its values ride extraParams), and the staff
 * scope-search input.
 *
 * Contains:
 * - RadioGrid       — <EntityGrid> over odl-app/radios/ (default export)
 * - OnlineDot       — small status indicator for staff rows
 * - PlacementDot    — solid color dot keyed off the dashboard card palette
 */

import { ApartmentOutlined, BusinessOutlined } from '@mui/icons-material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { fetchCustomerScopeSuggestions } from '@sophocles/api/customer';
import { listRadios } from '@sophocles/api/radios';
import { fetchSiteGroupScopeSuggestions } from '@sophocles/api/siteGroup';
import { useUser } from '@sophocles/app';
import AssignOrCreateDialog from '@sophocles/components/dialogs/AssignOrCreateDialog';
import ScopeSearchInput from '@sophocles/components/ScopeSearchInput';
import { EntityGrid } from '@sophocles/data-grids';
import { DataGridSingleSelectFilter } from '@sophocles/data-grids/components/filters';
import { ClearAllChip } from '@sophocles/data-grids/components/toolbar';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridQuickFilter from '@sophocles/data-grids/datasource/useGridQuickFilter';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import { sitePath } from '@sophocles/router/paths';
import { buildRadioGridParams } from '@sophocles/views/search/radioGridParams';
import {
  deriveLocationDisplay,
  PLACEMENT_CUSTOMER_INVENTORY,
  PLACEMENT_INVENTORY_LOCATION,
  PLACEMENT_NOT_INSTALLED,
  PLACEMENT_ON_SITE,
  PLACEMENT_PLANT_INVENTORY,
} from '@sophocles/views/search/radioLocationDisplay';

/** odl_app resource path for the radios grid (list + grid-spec). */
const RESOURCE_PATH = 'radios';

/** Hex palette mirrors the inventory dashboard summary cards. */
const PLACEMENT_DOT_COLOR = {
  [PLACEMENT_ON_SITE]: '#059669',
  [PLACEMENT_NOT_INSTALLED]: '#0284c7',
  [PLACEMENT_CUSTOMER_INVENTORY]: '#7c3aed',
  [PLACEMENT_PLANT_INVENTORY]: '#d97706',
};

/** MUI chip color mirrors the inventory dashboard grid's Placement column. */
const PLACEMENT_CHIP_COLOR = {
  [PLACEMENT_ON_SITE]: 'success',
  [PLACEMENT_PLANT_INVENTORY]: 'warning',
  [PLACEMENT_CUSTOMER_INVENTORY]: 'secondary',
  [PLACEMENT_INVENTORY_LOCATION]: 'info',
};

/** Online status dot — only rendered when online is a boolean (staff field). */
function OnlineDot({ online }) {
  const { t } = useTranslation();
  if (online == null) return null;
  return (
    <FiberManualRecordIcon
      sx={{ fontSize: 10, color: online ? 'success.main' : 'error.main', mr: 0.5, flexShrink: 0 }}
      aria-label={online ? t('radio.online', 'Online') : t('radio.offline', 'Offline')}
    />
  );
}

/** Solid color dot for placement options — matches the dashboard cards. */
function PlacementDot({ value, size = 10 }) {
  const color = PLACEMENT_DOT_COLOR[value] ?? 'transparent';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: color,
        flexShrink: 0,
      }}
    />
  );
}

export default function RadioGrid({ persistScope = 'radioGrid' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isStaff, permissions } = useUser();
  // RadioGrid owns the grid apiRef so the staff scope-search can drive the
  // quick filter (useGridQuickFilter) while EntityGrid runs the grid. EntityGrid
  // mounts the grid only after its spec resolves, so on a cold load the grid is
  // live after our first render — gridReady re-runs the quick-filter sync then.
  const apiRef = useGridApiRef();
  const [gridReady, setGridReady] = useState(false);
  // Permission to onboard an unassigned radio to a site — mirrors the
  // Inventory Dashboard's assign gate.
  const canAssignToSite = isStaff || permissions?.isReseller || permissions?.isAtLeastSiteGroupAdmin;

  const [customerFilter, setCustomerFilter] = usePersistedState(useGridSessionKey(persistScope, 'customer'), '');
  const [siteGroupFilter, setSiteGroupFilter] = usePersistedState(useGridSessionKey(persistScope, 'siteGroup'), '');
  const [onlineFilter, setOnlineFilter] = usePersistedState(useGridSessionKey(persistScope, 'online'), '');
  const [placementFilter, setPlacementFilter] = usePersistedState(useGridSessionKey(persistScope, 'placement'), null);
  // 'active' (default), 'deleted', or 'all'. Staff-only; non-staff users never
  // see this control and the backend defaults to all-active for them.
  const [deletedFilter, setDeletedFilter] = usePersistedState(useGridSessionKey(persistScope, 'deleted'), 'active');
  // Resolved labels for the scope-search chips so we can render names
  // (not raw ids) for persisted filters. Lives in session state — the
  // ids are the truth, labels are cosmetic.
  const [scopeLabels, setScopeLabels] = useState({ customer: null, siteGroup: null });
  // Row targeted by the Actions column's onboard button. Null when the
  // dialog is closed.
  const [onboardRow, setOnboardRow] = useState(null);

  const extraParams = useMemo(
    () =>
      buildRadioGridParams({ onlineFilter, customerFilter, siteGroupFilter, placementFilter, deletedFilter, isStaff }),
    [onlineFilter, customerFilter, siteGroupFilter, placementFilter, deletedFilter, isStaff],
  );

  const fetchFn = useCallback(params => listRadios(params), []);

  const handleRowClick = useCallback(
    ({ row }) => {
      // Staff reach the radio detail page via the ICCID cell link, not a
      // whole-row click. Non-staff rows open the unit's site.
      if (isStaff) return;
      const loc = row.location;
      if (!loc?.customer?.id || !loc?.site_group?.id || !loc?.site?.id) return;
      navigate(sitePath({ cust_id: loc.customer.id, sitegroup_id: loc.site_group.id, site_id: loc.site.id }));
    },
    [isStaff, navigate],
  );

  const placementLabelMap = useMemo(
    () => ({
      [PLACEMENT_ON_SITE]: t('inventory.onSite'),
      [PLACEMENT_PLANT_INVENTORY]: t('inventory.groupInventory'),
      [PLACEMENT_CUSTOMER_INVENTORY]: t('inventory.customerInventory'),
      [PLACEMENT_NOT_INSTALLED]: t('inventory.notInstalled'),
      [PLACEMENT_INVENTORY_LOCATION]: t('inventory.inventory'),
    }),
    [t],
  );

  // --- Render overrides on the descriptor columns ---

  const fieldOverrides = useMemo(
    () => ({
      iccid: {
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <OnlineDot online={row.online} />
            {isStaff ? (
              <Box
                component={Link}
                to={`/support/radios/${row.id}`}
                onClick={e => e.stopPropagation()}
                sx={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
              >
                <Typography variant="body2" noWrap sx={{ color: 'primary.main' }}>
                  {row.iccid}
                </Typography>
              </Box>
            ) : (
              <span>{row.iccid}</span>
            )}
          </Box>
        ),
      },
      placement_type: {
        flex: 0.7,
        renderCell: ({ row }) => {
          const label = placementLabelMap[row.placement_type];
          if (!label) return null;
          return <Chip size="small" color={PLACEMENT_CHIP_COLOR[row.placement_type] ?? 'default'} label={label} />;
        },
      },
      site_name: {
        flex: 1.2,
        minWidth: 220,
        renderCell: ({ row, value }) => {
          const { label, link } = deriveLocationDisplay(row.location, row.placement_type);
          const display = label || value;
          if (!display) return null;
          if (!link) {
            return (
              <Typography variant="body2" noWrap>
                {display}
              </Typography>
            );
          }
          return (
            <Box
              component={Link}
              to={link}
              onClick={e => e.stopPropagation()}
              sx={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
            >
              <Typography variant="body2" noWrap sx={{ color: 'primary.main' }}>
                {display}
              </Typography>
            </Box>
          );
        },
      },
      ip_address: { valueGetter: (_value, row) => row.ip_address ?? '' },
      kore_status: { valueGetter: (_value, row) => row.kore_status ?? '' },
      location_timestamp: {
        // Preserve the old 150px min width (the datetime renderer defaults to 160).
        minWidth: 150,
        renderCell: ({ row }) => {
          const value = row.location_timestamp;
          if (!value) return null;
          const dt = new Date(value);
          return (
            <Tooltip title={dt.toLocaleString()}>
              <Typography variant="caption" noWrap>
                {moment(dt).fromNow()}
              </Typography>
            </Tooltip>
          );
        },
      },
    }),
    [isStaff, placementLabelMap],
  );

  // Bespoke Onboard action column — not modeled in the descriptor (per-row
  // conditional + permission-gated view logic). Appended via extraColumns.
  const extraColumns = useMemo(() => {
    if (!canAssignToSite) return [];
    return [
      {
        field: 'actions',
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          if (row.placement_type === PLACEMENT_ON_SITE) return null;
          return (
            <Button
              size="small"
              variant="contained"
              onClick={e => {
                e.stopPropagation();
                setOnboardRow(row);
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('search.onboard', 'Onboard')}
            </Button>
          );
        },
      },
    ];
  }, [canAssignToSite, t]);

  const renderMobileCard = useCallback(
    row => {
      const { label } = deriveLocationDisplay(row.location, row.placement_type);
      return (
        <Box sx={{ py: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <OnlineDot online={row.online} />
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main', fontSize: 'var(--font-sm)' }}>
              {row.iccid}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--text-secondary)' }}>
            <PlacementDot value={row.placement_type} size={8} />
            <Box component="span" sx={{ fontSize: 'var(--font-xs)' }}>
              {label ?? t('radio.noLocation', 'No location')}
            </Box>
          </Box>
          {row.mac_address && (
            <Box sx={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)' }}>{row.mac_address}</Box>
          )}
        </Box>
      );
    },
    [t],
  );

  // --- Toolbar filters ---

  const onlineFilterOptions = useMemo(
    () => [
      { value: 'true', label: t('radio.online', 'Online') },
      { value: 'false', label: t('radio.offline', 'Offline') },
    ],
    [t],
  );

  const placementFilterOptions = useMemo(
    () => [
      { value: PLACEMENT_ON_SITE, label: t('inventory.onSite') },
      { value: PLACEMENT_NOT_INSTALLED, label: t('inventory.notInstalled') },
      { value: PLACEMENT_CUSTOMER_INVENTORY, label: t('inventory.customerInventory') },
      { value: PLACEMENT_PLANT_INVENTORY, label: t('inventory.groupInventory') },
    ],
    [t],
  );

  const deletedFilterOptions = useMemo(
    () => [
      { value: 'deleted', label: t('inventory.deleted', 'Deleted') },
      { value: 'all', label: t('general.all', 'All') },
    ],
    [t],
  );

  const renderPlacementOption = useCallback(
    option => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlacementDot value={option.value} />
        <span>{option.label}</span>
      </Box>
    ),
    [],
  );

  const placementChipIcon = placementFilter ? (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', pl: 0.25 }}>
      <PlacementDot value={placementFilter} size={8} />
    </Box>
  ) : undefined;

  const handleResetAll = useCallback(() => {
    setOnlineFilter('');
    setCustomerFilter('');
    setSiteGroupFilter('');
    setPlacementFilter(null);
    setDeletedFilter('active');
    setScopeLabels({ customer: null, siteGroup: null });
  }, [setOnlineFilter, setCustomerFilter, setSiteGroupFilter, setPlacementFilter, setDeletedFilter]);

  // --- Scope search (customer / site group inside the search input) ---

  const [searchText, setSearchText, commitSearch] = useGridQuickFilter(apiRef, gridReady);

  const scopeProviders = useMemo(() => {
    if (!isStaff) return [];
    return [
      {
        key: 'customer',
        label: t('customer.customer', 'Customer'),
        icon: <BusinessOutlined sx={{ fontSize: 18 }} />,
        isActive: () => Boolean(customerFilter),
        fetchSuggestions: async query => {
          const data = await fetchCustomerScopeSuggestions({
            offset: 0,
            limit: 5,
            is_prospect: false,
            text_search: `name|${query}`,
          });
          const list = data?.results ?? data ?? [];
          return list.map(c => ({ id: c.id, label: c.name, sublabel: c.description || undefined }));
        },
        applyValue: opt => {
          setCustomerFilter(opt.id);
          setSiteGroupFilter('');
          setScopeLabels(s => ({ ...s, customer: opt.label, siteGroup: null }));
        },
      },
      {
        key: 'site_group',
        label: t('siteGroups.siteGroup', 'Group'),
        icon: <ApartmentOutlined sx={{ fontSize: 18 }} />,
        isActive: () => Boolean(siteGroupFilter),
        fetchSuggestions: async query => {
          const params = { offset: 0, limit: 5, text_search: `name|${query}` };
          if (customerFilter) params.customer = customerFilter;
          const data = await fetchSiteGroupScopeSuggestions(params);
          const list = data?.results ?? data ?? [];
          return list.map(g => ({ id: g.id, label: g.name, sublabel: g.description || undefined }));
        },
        applyValue: opt => {
          setSiteGroupFilter(opt.id);
          setScopeLabels(s => ({ ...s, siteGroup: opt.label }));
        },
      },
    ];
  }, [isStaff, customerFilter, siteGroupFilter, setCustomerFilter, setSiteGroupFilter, t]);

  const scopeChips = useMemo(() => {
    const out = [];
    if (customerFilter) {
      out.push({
        key: 'customer',
        label: t('customer.customer', 'Customer'),
        displayValue: scopeLabels.customer || `#${customerFilter}`,
        icon: <BusinessOutlined sx={{ fontSize: 14 }} />,
        onRemove: () => {
          setCustomerFilter('');
          setSiteGroupFilter('');
          setScopeLabels({ customer: null, siteGroup: null });
        },
      });
    }
    if (siteGroupFilter) {
      out.push({
        key: 'site_group',
        label: t('siteGroups.siteGroup', 'Group'),
        displayValue: scopeLabels.siteGroup || `#${siteGroupFilter}`,
        icon: <ApartmentOutlined sx={{ fontSize: 14 }} />,
        onRemove: () => {
          setSiteGroupFilter('');
          setScopeLabels(s => ({ ...s, siteGroup: null }));
        },
      });
    }
    return out;
  }, [customerFilter, siteGroupFilter, scopeLabels, setCustomerFilter, setSiteGroupFilter, t]);

  const scopeSearch = isStaff ? (
    <ScopeSearchInput
      value={searchText}
      onValueChange={setSearchText}
      chips={scopeChips}
      providers={scopeProviders}
      placeholder={t('radio.searchPlaceholder', 'Search ICCID, MAC, customers, groups…')}
      onSubmit={commitSearch}
    />
  ) : undefined;

  const toolbarContent = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <DataGridSingleSelectFilter
        label={t('inventory.placement', 'Placement')}
        value={placementFilter}
        onChange={setPlacementFilter}
        options={placementFilterOptions}
        renderOption={renderPlacementOption}
        icon={placementChipIcon}
      />
      {isStaff && (
        <DataGridSingleSelectFilter
          label={t('radio.status', 'Status')}
          value={onlineFilter !== '' ? onlineFilter : null}
          onChange={next => setOnlineFilter(next ?? '')}
          options={onlineFilterOptions}
        />
      )}
      {isStaff && (
        <DataGridSingleSelectFilter
          label={t('general.deleted', 'Deleted')}
          value={deletedFilter === 'active' ? null : deletedFilter}
          onChange={next => setDeletedFilter(next ?? 'active')}
          options={deletedFilterOptions}
          allLabel={t('general.active', 'Active')}
          chipLabel={
            deletedFilter === 'active'
              ? t('general.active', 'Active')
              : deletedFilter === 'all'
                ? t('general.all', 'All')
                : t('inventory.deleted', 'Deleted')
          }
        />
      )}
      {(placementFilter ||
        onlineFilter !== '' ||
        customerFilter ||
        siteGroupFilter ||
        (isStaff && deletedFilter !== 'active')) && <ClearAllChip onClick={handleResetAll} />}
    </Box>
  );

  return (
    <>
      <EntityGrid
        basePath={RESOURCE_PATH}
        fieldOverrides={fieldOverrides}
        fetchFn={fetchFn}
        extraParams={extraParams}
        options={{
          gridApiRef: apiRef,
          onGridReady: () => setGridReady(true),
          infiniteScroll: true,
          persistKey: persistScope,
          defaultSort: [{ field: 'iccid', sort: 'asc' }],
          onRowClick: handleRowClick,
          searchSlot: scopeSearch,
          searchPlaceholder: t('radio.searchPlaceholder', 'Search ICCID or MAC'),
          toolbarContent,
          mobile: { renderCard: renderMobileCard },
          extraColumns,
          countNoun: t('radio.radios', 'radios'),
        }}
      />
      <AssignOrCreateDialog
        open={onboardRow != null}
        onClose={() => setOnboardRow(null)}
        radioId={onboardRow?.id}
        radioLabel={onboardRow?.iccid || onboardRow?.mac_address || (onboardRow ? `#${onboardRow.id}` : undefined)}
        customerId={onboardRow?.location?.customer?.id || undefined}
        siteGroupId={onboardRow?.location?.site_group?.id || undefined}
      />
    </>
  );
}
